import { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { appFirebaseAuth } from '@/context';
import { MapPin, Globe, DollarSign, Menu, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { CircleUserIcon, SearchIcon, AddTripCard } from "./Dashboard/components";
// import { TraderGraphIcon } from "./Dashboard/components/TraderGraphIcon";
import { Sidebar } from "@/components/Sidebar";
import { signOut } from 'firebase/auth';
import { useTrips } from '../context/TripsContext';

export const TripSelection = (): React.ReactNode => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { trips, fetchUserTrips } = useTrips();
  // const [editingNickname, setEditingNickname] = useState<string | null>(null);
  // const [newNickname, setNewNickname] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    fetchUserTrips();
  }, [fetchUserTrips]);

  // const handleEditNickname = (tripId: string) => {
  //   const trip = trips.find(trip => trip.id === tripId);
  //   if (trip) {
  //     setEditingNickname(tripId);
  //     setNewNickname(trip.nickname || '');
  //   }
  // };

  // const handleSaveNickname = async (tripId: string) => {
  //   const trip = trips.find(trip => trip.id === tripId);
  //   if (trip && newNickname.trim() !== trip.nickname) {
  //     try {
  //       await updateTrip(tripId, { nickname: newNickname });
  //     } catch (error) {
  //       console.error('Error updating nickname:', error);
  //     }
  //   }
  //   setEditingNickname(null);
  // };

  const handleLogoutClick = async () => {
    signOut(appFirebaseAuth);
    navigate('/login');
  }
  
  return (
    <div className="flex min-h-screen w-full">
      {/* Desktop Sidebar - Hidden on mobile */}
      <div className="hidden md:block flex-shrink-0 fixed left-0 top-0 bottom-0 z-30">
        <Sidebar />
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden animate-in fade-in duration-200"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          {/* Sidebar */}
          <div className="fixed left-0 top-0 bottom-0 w-3/4 max-w-sm z-50 md:hidden animate-in slide-in-from-left duration-300">
            <Sidebar />
          </div>
        </>
      )}

      <div className="flex min-h-screen w-full flex-col md:ml-64">
      <header className="sticky top-0 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6">
        {/* Hamburger Menu - Only on mobile */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden flex-shrink-0"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
          <span className="sr-only">Toggle menu</span>
        </Button>

        <div className="flex w-full items-center gap-4 md:gap-2 lg:gap-4">
          <form className="ml-auto flex-1 sm:flex-initial">
            <div className="relative">
              <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search trips..."
                className="pl-8 sm:w-[300px] md:w-[200px] lg:w-[300px]"
              />
            </div>
          </form>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="icon" className="rounded-full">
                <CircleUserIcon className="h-5 w-5" />
                <span className="sr-only">Toggle user menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>Settings</DropdownMenuItem>
              <DropdownMenuItem>Support</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogoutClick}>Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <main className="flex flex-1 flex-col gap-4 p-4 md:gap-8 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Add Trip Card - Always first */}
          <AddTripCard />

          {/* Trips sorted by creation date (most recent first) */}
          {trips && [...trips]
            .sort((a, b) => {
              const dateA = new Date(a.created_at || 0).getTime();
              const dateB = new Date(b.created_at || 0).getTime();
              return dateB - dateA; // Most recent first
            })
            .map(trip => (
            <Card
              key={trip.id}
              className="hover:shadow-lg transition-shadow cursor-pointer group"
              onClick={() => navigate(`/trip/${trip.short_id}`)}
            >
              <CardContent className="flex flex-col items-center justify-center h-48 relative pt-6">
                <MapPin className="h-8 w-8 mb-3 text-muted-foreground" />
                <span className="text-2xl font-bold text-center px-4">{trip.nickname}</span>

                {/* Destination and Currency Info */}
                <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Globe className="h-4 w-4" />
                    <span>{trip.destination || 'World'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <DollarSign className="h-4 w-4" />
                    <span>{trip.currency || '$'}</span>
                  </div>
                </div>

                {/* Edit nickname functionality temporarily disabled
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditNickname(trip.id);
                  }}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                */}
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
      </div>
    </div>
  );
};

